import { initializeApp, getApps, type FirebaseApp, type FirebaseOptions } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'
import { logger } from './logger'

export interface FirebaseClient {
  app: FirebaseApp
  auth: Auth
  db: Firestore
  functions: Functions
}

export interface CreateFirebaseClientOptions {
  /** Cloud Functions region. Default `europe-west1`. */
  functionsRegion?: string
  /**
   * IndexedDB-backed multi-tab persistent cache. Default `true`. Falls back to
   * the in-memory default if IndexedDB is unavailable (private mode, quota,
   * older browser). Set `false` for surfaces that don't want offline reads.
   */
  persistentCache?: boolean
  /** Persistent cache size. Default 50 MB. */
  cacheSizeBytes?: number
}

/**
 * Create the Firebase client singletons for an app from its own project
 * config. App-agnostic: each app (clubmanager, usersmatch) calls this once
 * from its own `core/firebase` layer with its own `FirebaseOptions`, then
 * exports the returned `app`/`auth`/`db`/`functions`.
 *
 * `initializeFirestore` MUST run before any other Firestore call, so the
 * calling module should be the single init site. On failure (private mode,
 * quota, missing IDB) it falls back to the in-memory default — the app stays
 * usable, just without offline reads/writes.
 */
export function createFirebaseClient(
  config: FirebaseOptions,
  options: CreateFirebaseClientOptions = {},
): FirebaseClient {
  const {
    functionsRegion = 'europe-west1',
    persistentCache = true,
    cacheSizeBytes = 50 * 1024 * 1024,
  } = options

  const app: FirebaseApp = getApps()[0] ?? initializeApp(config)
  const auth: Auth = getAuth(app)

  const db: Firestore = (() => {
    if (!persistentCache) return initializeFirestore(app, {})
    try {
      return initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
          cacheSizeBytes,
        }),
      })
    } catch (err) {
      logger.w('Firestore persistent cache unavailable; falling back to memory cache', err)
      return initializeFirestore(app, {})
    }
  })()

  const functions: Functions = getFunctions(app, functionsRegion)

  return { app, auth, db, functions }
}
